import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { ContactDetailPage } from '@/pages/ContactDetailPage';
import { ProposalsPage } from '@/pages/ProposalsPage';
import { ProposalDetailPage } from '@/pages/ProposalDetailPage';
import { AdminPage } from '@/pages/AdminPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { EventFormPage } from '@/pages/EventFormPage';
import { EventDetailPage } from '@/pages/EventDetailPage';
import { EventEditPage } from '@/pages/EventEditPage';
import { DokumentePage } from '@/pages/DokumentePage';
import { BauvorhabenPage } from '@/pages/BauvorhabenPage';
import { BauvorhabenDetailPage } from '@/pages/BauvorhabenDetailPage';
import { GewerkeAdminPage } from '@/pages/GewerkeAdminPage';
import { ModuleDisabledPage } from '@/pages/ModuleDisabledPage';

function ProtectedRoute({
  children,
  requireAdmin = false,
  requireApprover = false,
  requireModule
}:
  {
    children: React.ReactNode;
    requireAdmin?: boolean;
    requireApprover?: boolean;
    requireModule?: string;
  }) {
  const { user: _user, isLoading, isAuthenticated, isAdmin, isApprover, hasModule } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  if (requireApprover && !isApprover) {
    return <Navigate to="/" replace />;
  }

  // Deaktiviertes Modul: Seite statt Redirect — `/` selbst ist die
  // Kontakte-Seite, ein Redirect könnte also in eine Schleife führen.
  if (requireModule && !hasModule(requireModule)) {
    return <ModuleDisabledPage moduleKey={requireModule} />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<ProtectedRoute requireModule="kontakte"><ContactsPage /></ProtectedRoute>} />
          <Route path="contacts/:id" element={<ProtectedRoute requireModule="kontakte"><ContactDetailPage /></ProtectedRoute>} />
          <Route path="calendar" element={<ProtectedRoute requireModule="kalender"><CalendarPage /></ProtectedRoute>} />
          <Route path="calendar/new" element={<ProtectedRoute requireModule="kalender"><EventFormPage /></ProtectedRoute>} />
          <Route path="calendar/:id" element={<ProtectedRoute requireModule="kalender"><EventDetailPage /></ProtectedRoute>} />
          <Route path="calendar/:id/edit" element={<ProtectedRoute requireModule="kalender"><EventEditPage /></ProtectedRoute>} />
          <Route path="dokumente" element={<ProtectedRoute requireModule="dokumente"><DokumentePage /></ProtectedRoute>} />
          <Route path="bauvorhaben" element={<ProtectedRoute requireModule="bauvorhaben"><BauvorhabenPage /></ProtectedRoute>} />
          {/* Statisches Segment gewinnt gegen :id — Katalog-Verwaltung, approver+. */}
          <Route path="bauvorhaben/gewerke" element={
            <ProtectedRoute requireModule="bauvorhaben" requireApprover><GewerkeAdminPage /></ProtectedRoute>
          } />
          {/* "/bauvorhaben/new" läuft bewusst über :id (wie /contacts/new) —
              die DetailPage behandelt id === 'new' als Anlege-Modus. */}
          <Route path="bauvorhaben/:id" element={<ProtectedRoute requireModule="bauvorhaben"><BauvorhabenDetailPage /></ProtectedRoute>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="proposals" element={<ProposalsPage />} />
          <Route path="proposals/:id" element={<ProposalDetailPage />} />
          <Route path="admin" element={
            <ProtectedRoute requireApprover>
              <AdminPage />
            </ProtectedRoute>
          } />
        </Route>
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
